// src/pages/WorkByPersonPage.js
//
// Manager view: for a chosen month, what each employee and each freelancer worked on across all
// clients — their deliverables (client, type, quantity, date) and the cost attributed to them.
// Gated to Manager + Sales Manager (shows costs).

import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs, orderBy, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { deliverableLabel, deliverableIcon } from '../config/deliverables';
import styles from '../styles/WorkByPersonPage.module.css';

const isTerminated = (u) =>
  (u.employmentStatus || (u.active === false ? 'Terminated' : 'Active')) === 'Terminated';
const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const PRESETS = [
  { key: 'all', ar: 'الكل', en: 'All' },
  { key: 'thisMonth', ar: 'هذا الشهر', en: 'This month' },
  { key: 'lastMonth', ar: 'الشهر الماضي', en: 'Last month' },
  { key: 'last28', ar: 'آخر 28 يوم', en: 'Last 28 days' },
  { key: 'thisWeek', ar: 'هذا الأسبوع', en: 'This week' },
  { key: 'lastWeek', ar: 'الأسبوع الماضي', en: 'Last week' },
  { key: 'last7', ar: 'آخر 7 أيام', en: 'Last 7 days' },
  { key: 'today', ar: 'اليوم', en: 'Today' },
  { key: 'yesterday', ar: 'أمس', en: 'Yesterday' },
  { key: 'thisYear', ar: 'هذه السنة', en: 'This year' },
  { key: 'lastYear', ar: 'السنة الماضية', en: 'Last year' },
  { key: 'custom', ar: 'مخصص', en: 'Custom' },
];

// Returns { start, end } as YYYY-MM-DD (inclusive), or null for an open bound. Week starts Saturday.
const rangeFor = (preset, customStart, customEnd) => {
  const today = new Date();
  const daysSinceSat = (today.getDay() + 1) % 7; // Sat=0, Sun=1 … Fri=6
  const weekStart = addDays(today, -daysSinceSat);
  const y = today.getFullYear(), m = today.getMonth();
  switch (preset) {
    case 'all': return { start: null, end: null };
    case 'today': return { start: ymd(today), end: ymd(today) };
    case 'yesterday': { const d = addDays(today, -1); return { start: ymd(d), end: ymd(d) }; }
    case 'last7': return { start: ymd(addDays(today, -6)), end: ymd(today) };
    case 'last28': return { start: ymd(addDays(today, -27)), end: ymd(today) };
    case 'thisWeek': return { start: ymd(weekStart), end: ymd(addDays(weekStart, 6)) };
    case 'lastWeek': return { start: ymd(addDays(weekStart, -7)), end: ymd(addDays(weekStart, -1)) };
    case 'thisMonth': return { start: ymd(new Date(y, m, 1)), end: ymd(new Date(y, m + 1, 0)) };
    case 'lastMonth': return { start: ymd(new Date(y, m - 1, 1)), end: ymd(new Date(y, m, 0)) };
    case 'thisYear': return { start: `${y}-01-01`, end: `${y}-12-31` };
    case 'lastYear': return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
    case 'custom': return { start: customStart || null, end: customEnd || null };
    default: return { start: null, end: null };
  }
};
const inRange = (dateStr, start, end) => {
  if (!dateStr) return false;
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
};

const T = {
  ar: {
    title: 'العمل حسب الشخص', sub: 'شو اشتغل كل موظف وفريلانسر خلال الفترة',
    period: 'الفترة', from: 'من', to: 'إلى',
    employees: 'الموظفون', freelancers: 'الفريلانسرز',
    count: 'عدد الشغلات', cost: 'المستحق', currency: '₪',
    client: 'الزبون', type: 'النوع', qty: 'العدد', date: 'التاريخ',
    none: 'لا يوجد شغل مسجّل بهذه الفترة.', noEmp: 'لا يوجد موظفون بهذه الفترة.', noFree: 'لا يوجد فريلانسرز بهذه الفترة.',
    paid: 'المدفوع', remaining: 'المتبقّي', payments: 'الدفعات', addPayment: 'إضافة دفعة',
    amount: 'المبلغ', note: 'ملاحظة (اختياري)', del: 'حذف', add: 'إضافة', noPayments: 'لا يوجد دفعات بهذه الفترة.',
  },
  en: {
    title: 'Work by Person', sub: 'What each employee and freelancer worked on in the period',
    period: 'Period', from: 'From', to: 'To',
    employees: 'Employees', freelancers: 'Freelancers',
    count: 'Deliverables', cost: 'Earned', currency: '₪',
    client: 'Client', type: 'Type', qty: 'Qty', date: 'Date',
    none: 'No work recorded in this period.', noEmp: 'No employees in this period.', noFree: 'No freelancers in this period.',
    paid: 'Paid', remaining: 'Remaining', payments: 'Payments', addPayment: 'Add payment',
    amount: 'Amount', note: 'Note (optional)', del: 'Delete', add: 'Add', noPayments: 'No payments in this period.',
  },
};

export default function WorkByPersonPage() {
  const { language, isRTL, toggleLanguage } = useSocialLanguage();
  const { currentUser } = useAuth();
  const t = T[language] || T.ar;

  const [preset, setPreset] = useState('thisMonth');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const { start, end } = useMemo(() => rangeFor(preset, customStart, customEnd), [preset, customStart, customEnd]);

  const [deliverables, setDeliverables] = useState([]);
  const [comp, setComp] = useState({});       // uid -> { dailyCost, name }
  const [freeRates, setFreeRates] = useState({}); // id -> rates map
  const [payments, setPayments] = useState([]); // personPayments within the range
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // person key -> bool
  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Deliverables in the date range (date is a YYYY-MM-DD string → range query, no index needed).
        let dq = collection(db, 'deliverables');
        if (start && end) dq = query(dq, where('date', '>=', start), where('date', '<=', end));
        else if (start) dq = query(dq, where('date', '>=', start));
        else if (end) dq = query(dq, where('date', '<=', end));

        const [dSnap, uSnap, fSnap, pSnap] = await Promise.all([
          getDocs(dq),
          getDocs(query(collection(db, 'users'), orderBy('name', 'asc'))),
          getDocs(collection(db, 'freelancers')),
          getDocs(collection(db, 'personPayments')),
        ]);
        if (cancelled) return;
        setDeliverables(dSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const cm = {};
        uSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !isTerminated(u)).forEach((u) => {
          const s = num(u.compensation?.monthlySalary);
          const wd = num(u.compensation?.workingDaysPerMonth);
          cm[u.id] = { dailyCost: s && wd ? s / wd : 0, name: u.name || u.email || '' };
        });
        setComp(cm);
        const fr = {};
        fSnap.docs.forEach((d) => { fr[d.id] = d.data().rates || {}; });
        setFreeRates(fr);
        const pays = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => (!start && !end) ? true : inRange(p.date, start, end));
        setPayments(pays);
      } catch (e) { console.error('load work-by-person failed', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [start, end, reload]);

  // Payments grouped by person key (`kind:id`).
  const paidByPerson = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      const key = `${p.personKind}:${p.personId}`;
      if (!map[key]) map[key] = { total: 0, list: [] };
      map[key].total += num(p.amount);
      map[key].list.push(p);
    });
    Object.values(map).forEach((m) => m.list.sort((a, b) => (b.date || '').localeCompare(a.date || '')));
    return map;
  }, [payments]);

  const addPayment = async (person, amount, note) => {
    if (num(amount) <= 0) return;
    const payDate = ymd(new Date());
    try {
      await addDoc(collection(db, 'personPayments'), {
        personKind: person.kind, personId: person.id, personName: person.name || '',
        month: payDate.slice(0, 7), amount: num(amount), note: (note || '').trim(),
        date: payDate,
        createdBy: currentUser?.uid || null,
        createdByName: currentUser?.displayName || currentUser?.email || '',
        createdAt: serverTimestamp(),
      });
      bump();
    } catch (e) { console.error('add payment failed', e); }
  };
  const removePayment = async (id) => {
    try { await deleteDoc(doc(db, 'personPayments', id)); bump(); }
    catch (e) { console.error('remove payment failed', e); }
  };

  const { employees, freelancers } = useMemo(() => {
    // Per-employee per-day item counts (for daily-cost split).
    const empDayItems = {};
    deliverables.forEach((d) => {
      const qty = num(d.quantity) || 1;
      (d.executors || []).forEach((ex) => {
        if (ex.kind === 'employee' && ex.id) {
          const k = `${ex.id}|${d.date}`;
          empDayItems[k] = (empDayItems[k] || 0) + qty;
        }
      });
    });

    const people = {};
    deliverables.forEach((d) => {
      const qty = num(d.quantity) || 1;
      (d.executors || []).forEach((ex) => {
        if (!ex.id) return;
        const key = `${ex.kind}:${ex.id}`;
        if (!people[key]) people[key] = { kind: ex.kind, id: ex.id, name: ex.name || '—', count: 0, cost: 0, items: [] };
        let share = 0;
        if (ex.kind === 'employee') {
          const daily = comp[ex.id]?.dailyCost || 0;
          const cnt = empDayItems[`${ex.id}|${d.date}`] || qty;
          share = (daily / cnt) * qty;
        } else {
          const per = (ex.costOverride !== undefined && ex.costOverride !== null && ex.costOverride !== '')
            ? num(ex.costOverride) : num(freeRates[ex.id]?.[d.type]);
          share = per * qty;
        }
        people[key].count += qty;
        people[key].cost += share;
        people[key].items.push({ clientName: d.clientName || '—', type: d.type, qty, date: d.date, cost: share });
      });
    });

    const all = Object.entries(people).map(([key, p]) => ({ key, ...p }));
    all.forEach((p) => p.items.sort((a, b) => (b.date || '').localeCompare(a.date || '')));
    const byCost = (a, b) => b.cost - a.cost;
    return {
      employees: all.filter((p) => p.kind === 'employee').sort(byCost),
      freelancers: all.filter((p) => p.kind === 'freelancer').sort(byCost),
    };
  }, [deliverables, comp, freeRates]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const PersonCard = (p) => {
    const pay = paidByPerson[p.key] || { total: 0, list: [] };
    const remaining = p.cost - pay.total;
    return (
    <div key={p.key} className={styles.personCard}>
      <button className={styles.personHead} onClick={() => toggle(p.key)}>
        <span className={styles.personName}>
          {p.kind === 'freelancer' ? '🧑‍💻' : '👤'} {p.name}
        </span>
        <span className={styles.personStats}>
          <span className={styles.countPill}>{p.count} {t.count}</span>
          <span className={styles.costPill}>{p.cost.toFixed(0)} {t.currency}</span>
          <span className={styles.paidPill}>{t.paid}: {pay.total.toFixed(0)}</span>
          <span className={`${styles.remainPill} ${remaining > 0 ? styles.remainDue : styles.remainClear}`}>
            {t.remaining}: {remaining.toFixed(0)}
          </span>
          <span className={styles.chevron}>{expanded[p.key] ? '▲' : '▼'}</span>
        </span>
      </button>
      {expanded[p.key] && (
        <div className={styles.itemsWrap}>
          <table className={styles.itemsTable}>
            <thead>
              <tr><th>{t.client}</th><th>{t.type}</th><th>{t.qty}</th><th>{t.date}</th><th>{t.cost}</th></tr>
            </thead>
            <tbody>
              {p.items.map((it, i) => (
                <tr key={i}>
                  <td data-label={t.client}>{it.clientName}</td>
                  <td data-label={t.type}>{deliverableIcon(it.type)} {deliverableLabel(it.type, language)}</td>
                  <td data-label={t.qty}>×{it.qty}</td>
                  <td data-label={t.date}>{it.date}</td>
                  <td data-label={t.cost}>{it.cost.toFixed(0)} {t.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Payments to this person within the period */}
          <div className={styles.paymentsBlock}>
            <div className={styles.paymentsTitle}>{t.payments}</div>
            {pay.list.length === 0 ? (
              <p className={styles.muted}>{t.noPayments}</p>
            ) : (
              <ul className={styles.paymentList}>
                {pay.list.map((pm) => (
                  <li key={pm.id} className={styles.paymentItem}>
                    <span>{num(pm.amount).toFixed(0)} {t.currency}{pm.note ? ` — ${pm.note}` : ''} <em className={styles.payDate}>({pm.date})</em></span>
                    <button className={styles.linkDel} onClick={() => removePayment(pm.id)}>{t.del}</button>
                  </li>
                ))}
              </ul>
            )}
            <PaymentAdder t={t} onAdd={(amount, note) => addPayment(p, amount, note)} />
          </div>
        </div>
      )}
    </div>
    );
  };

  return (
    <div className={styles.page} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>{t.title}</h1>
          <p className={styles.subtitle}>{t.sub}</p>
        </div>
        <div className={styles.controls}>
          <select className={styles.monthInput} value={preset} onChange={(e) => setPreset(e.target.value)}>
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{language === 'ar' ? p.ar : p.en}</option>)}
          </select>
          {preset === 'custom' && (
            <>
              <input type="date" className={styles.monthInput} value={customStart} max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)} title={t.from} />
              <input type="date" className={styles.monthInput} value={customEnd} min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)} title={t.to} />
            </>
          )}
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
      </div>

      {loading ? <p className={styles.muted}>…</p> : (deliverables.length === 0 ? <p className={styles.muted}>{t.none}</p> : (
        <>
          <h3 className={styles.sectionHeading}>👤 {t.employees}</h3>
          {employees.length === 0 ? <p className={styles.muted}>{t.noEmp}</p> : employees.map(PersonCard)}

          <h3 className={styles.sectionHeading}>🧑‍💻 {t.freelancers}</h3>
          {freelancers.length === 0 ? <p className={styles.muted}>{t.noFree}</p> : freelancers.map(PersonCard)}
        </>
      ))}
    </div>
  );
}

// Small inline form to record a payment to a person.
function PaymentAdder({ t, onAdd }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const submit = () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return;
    onAdd(amount, note);
    setAmount(''); setNote('');
  };
  return (
    <div className={styles.paymentAdd}>
      <input type="number" min="0" placeholder={t.amount} value={amount} onChange={(e) => setAmount(e.target.value)} />
      <input placeholder={t.note} value={note} onChange={(e) => setNote(e.target.value)} />
      <button className={styles.addBtn} onClick={submit}>{t.addPayment}</button>
    </div>
  );
}
